import React, { useContext, useEffect, useState, useMemo } from "react";
import { useRevalidator, useNavigate } from "react-router-dom";
import {
  Box, UnorderedList, ListItem,
  Icon,
  Avatar,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  Badge,
  HStack,
  VStack,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Textarea,
  Switch,
  FormErrorMessage,
  FormHelperText,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import {
  Card, CardHeader, Right, CardContent, CardTitle
} from "../../../../components/Card";
import Link from "../../../../components/Link";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { 
  FiUsers, 
  FiBriefcase, 
  FiPhone, 
  FiMail, 
  FiShield, 
  FiEdit3, 
  FiTrash2, 
  FiMoreVertical,
  FiUser,
  FiSettings
} from "react-icons/fi";
import getFullName from "../../../../helpers/FullName";
import { AddIcon } from "@chakra-ui/icons";
import AuthContext from "../../../../contexts/Auth";

// Phone number formatting function
const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Sadece rakamları al
  const numbers = value.replace(/\D/g, '');
  
  // 10 haneli sınır
  const limited = numbers.slice(0, 10);
  
  // Formatla: 5XX XXX XX XX
  if (limited.length >= 7) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6, 8)} ${limited.slice(8, 10)}`;
  } else if (limited.length >= 4) {
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
  } else if (limited.length >= 1) {
    return `${limited.slice(0, 3)} ${limited.slice(3)}`;
  }
  return limited;
};

const cleanPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\s/g, '');
};

const RelatedTasks = ({ employee }) => {

  const loadTasks = async () => {
    const results = await Promise.all(
      employee.team_memberships.map((membership) => client.get(
        '/tasks/', {
          'params': {
            'assigned_to': urlToId(membership.team.url)
          }
        }
      ).then((resp) => resp.data.results))
    )
    // Thanks to: https://stackoverflow.com/a/6768642/23782599
    const joined = [].concat.apply([], results)
    return setTasks(joined)
  }

  const [tasks, setTasks] = useState([])

  useEffect(() => { loadTasks() }, []);

  return (
    <UnorderedList>
      {tasks && tasks.map((task) => (
        <ListItem>
          <Link to={'/is-emri/' + urlToId(task.ticket) + '/?gorev=' + urlToId(task.url)}>{task.title}</Link>
        </ListItem>
      ))}
    </UnorderedList>
  )
}

const AddToTeamModal = ({ selectedEmployee, disclosure, revalidator, onEmployeeUpdated }) => {
  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState(false);
  const [errors, setErrors] = useState({});
  const auth = useContext(AuthContext);
  const toast = useToast();
  const fetchTeams = () => {
    const resellerUrl = auth.data.user.employee?.reseller.url;
    if (!resellerUrl) return;
    const resellerId = urlToId(resellerUrl);
    client.get("/teams/?reseller=" + resellerId).then((response) => {
      setTeamOptions(
        response.data.map((team) => ({
          value: team.url,
          label: team.name,
        }))
      );
    });
  };
  const handleTeamSelect = (teamUrl) => {
    setSelectedTeam(teamUrl);
  };
  const handleLeadSelect = (isLead) => {
    setSelectedLeadStatus(isLead);
  };
  const handleSave = () => {
    client
      .post(`/members/`, {
        is_lead: selectedLeadStatus,
        employee: selectedEmployee.url,
        team: selectedTeam,
      })
      .then(async () => {
        try {
          if (onEmployeeUpdated && selectedEmployee?.url) {
            const resp = await client.get(selectedEmployee.url, { disableCache: true });
            const updated = Array.isArray(resp?.data) ? resp.data[0] : resp.data;
            if (updated) onEmployeeUpdated(updated);
          } else {
            revalidator.revalidate();
          }
        } catch (_) {
          revalidator.revalidate();
        }
        disclosure.onClose();
        toast({
          title: "Başarılı",
          description: `Takıma eklendi`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        setErrors(error.response.data);
      });
  };
  useEffect(() => {
    fetchTeams();
    setErrors("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosure.isOpen]);
  if (!selectedEmployee) {
    return;
  }
  return (
    <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Takıma Ekle</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl>
            <FormLabel>Personel</FormLabel>
            <Input value={getFullName(selectedEmployee.user)} disabled />
          </FormControl>
          <FormControl>
            <FormLabel>Takım</FormLabel>
            <Select
              value={teamOptions.label}
              defaultValue={teamOptions}
              onChange={(e) => handleTeamSelect(e.target.value)}
              placeholder="Takım seçiniz"
            >
              {teamOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl mt={4}>
            <Checkbox onChange={(e) => handleLeadSelect(e.target.checked)}>
              Takım lideri
            </Checkbox>
          </FormControl>
          {errors.non_field_errors &&
            errors.non_field_errors.map((e, index) => (
              <Text key={`error-${index}`} color="red">
                {e}
              </Text>
            ))}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={disclosure.onClose}>Vazgeç</Button>
            <Button
              onClick={() => handleSave(selectedEmployee.url)}
              colorScheme="blue"
              mr={3}
            >
              Ekle
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const CreateTeamModal = ({ revalidator, disclosure }) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const auth = useContext(AuthContext);
  const toast = useToast();

  const handleCreate = () => {
    setLoading(true);
    const resellerUrl = auth.data.user.employee?.reseller.url;
    if (!resellerUrl) return;
    const requestBody = { name: name, reseller: resellerUrl };
    client
      .post(`/teams/`, requestBody)
      .then(() => {
        disclosure.onClose();
        revalidator.revalidate();
        toast({
          title: "Başarılı",
          description: `${name} adlı takım oluşturuldu`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        setErrors(error.response.data);
      })
      .finally(() => {
        setName("");
        setLoading(false);
      });
  };

  // Reset errors when the modal is opened
  useEffect(() => {
    setErrors({});
  }, [disclosure.isOpen]);
  return (
    <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Takım oluştur</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl>
            <FormLabel>Takım Adı</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder=""
            />
              {errors.name &&
              errors.name.map((e) => (
                <Text key={e} color="red">
                  {e}
                </Text>
              ))}
          </FormControl>
          {errors.non_field_errors &&
            errors.non_field_errors.map((e) => (
              <Text key={e} color="red">
                {e}
              </Text>
            ))}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={handleCreate} isLoading={loading} colorScheme="blue" mr={3}>Oluştur</Button>
            <Button onClick={disclosure.onClose}>Vazgeç</Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

function RemoveFromTeamModal({employee, membership, callback, disclosure, isLoading}) {
  if (!employee || !membership) return;
  return (
    <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Takımdan Çıkar</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          "{getFullName(employee.user)}" adlı Personelı "{membership.team?.name || "-"}"
          adlı takımdan çıkartmak istediğinize emin misiniz?
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={disclosure.onClose}>Hayır</Button>
            <Button onClick={() => { callback(membership, disclosure); }} isLoading={isLoading} colorScheme="blue" mr={3}>Evet</Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Edit Employee Modal
const EditEmployeeModal = ({ employee, disclosure, revalidator }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    internal_number: '',
    is_manager: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const toast = useToast();
  const cardBg = 'white';
  const borderColor = 'gray.200';

  useEffect(() => {
    if (employee && disclosure.isOpen) {
      const user = employee.user || {};
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: (user.phone || '').replace('+90', '') || '',
        internal_number: employee.internal_number || '',
        is_manager: employee.is_manager || false
      });
      setErrors({});
      setTouched({});

      // Fetch full user details to populate missing fields like email/phone
      if (user.url) {
        client.get(user.url).then((resp) => {
          const data = resp?.data;
          const fullUser = Array.isArray(data) ? data[0] : data;
          setFormData((prev) => ({
            ...prev,
            first_name: fullUser?.first_name ?? prev.first_name,
            last_name: fullUser?.last_name ?? prev.last_name,
            email: fullUser?.email ?? prev.email,
            phone_number: (fullUser?.phone || prev.phone_number || '').replace('+90', '')
          }));
        }).catch(() => {
          // ignore
        });
      }
    }
  }, [employee, disclosure.isOpen]);

  const handleInputChange = (field, value) => {
    if (field === 'phone_number') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
      validateField('phone_number', formatted);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validateField = (field, value) => {
    const newErrors = { ...errors };
    
    if (field === 'phone_number') {
      const cleanValue = value.replace(/\s/g, '');
      if (!cleanValue) {
        newErrors.phone_number = 'Telefon numarası gereklidir';
      } else if (!/^5\d{9}$/.test(cleanValue)) {
        newErrors.phone_number = 'Geçerli bir telefon numarası giriniz (5XX XXX XX XX)';
      } else {
        delete newErrors.phone_number;
      }
    }
    
    setErrors(newErrors);
  };
  
  const handleFieldBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleSave = async () => {
    setLoading(true);
    setErrors({});
    
    try {
      const cleanedPhoneNumber = cleanPhoneNumber(formData.phone_number);
      const requestBody = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone_number: cleanedPhoneNumber ? `+90${cleanedPhoneNumber}` : '',
        internal_number: formData.internal_number,
        is_manager: formData.is_manager
      };
      
      await client.patch(`/employees/${employee.id}/`, requestBody);
      
      toast({
        title: 'Başarılı',
        description: 'Personel bilgileri güncellendi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      disclosure.onClose();
      revalidator.revalidate();
    } catch (error) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
      toast({
        title: 'Hata',
        description: 'Güncelleme sırasında bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={disclosure.isOpen} onClose={disclosure.onClose} size="lg">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent
        bg={cardBg}
        borderRadius="2xl"
        border="1px solid"
        borderColor={borderColor}
      >
        <ModalHeader>
          <HStack spacing={3}>
            <Icon as={FiEdit3} color="blue.500" />
            <Text>Personel Bilgilerini Düzenle</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <HStack spacing={4} width="full">
              <FormControl isInvalid={errors.first_name}>
                <FormLabel fontSize="sm" fontWeight="medium">Ad</FormLabel>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  borderRadius="lg"
                />
                <FormErrorMessage>{errors.first_name}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={errors.last_name}>
                <FormLabel fontSize="sm" fontWeight="medium">Soyad</FormLabel>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  borderRadius="lg"
                />
                <FormErrorMessage>{errors.last_name}</FormErrorMessage>
              </FormControl>
            </HStack>
            
            <FormControl isInvalid={errors.email}>
              <FormLabel fontSize="sm" fontWeight="medium">E-posta</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                borderRadius="lg"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
            
            <HStack spacing={4} width="full">
              <FormControl isInvalid={touched.phone_number && errors.phone_number}>
                <FormLabel fontSize="sm" fontWeight="medium">Telefon</FormLabel>
                <Input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => handleInputChange('phone_number', e.target.value)}
                  onBlur={() => handleFieldBlur('phone_number')}
                  placeholder="5XX XXX XX XX"
                  maxLength={13}
                  borderRadius="lg"
                  borderColor={touched.phone_number && errors.phone_number ? "red.300" : "gray.300"}
                />
                {touched.phone_number && errors.phone_number && (
                  <FormHelperText color="red.500">{errors.phone_number}</FormHelperText>
                )}
              </FormControl>
              <FormControl isInvalid={errors.internal_number}>
                <FormLabel fontSize="sm" fontWeight="medium">Dahili Numara</FormLabel>
                <Input
                  value={formData.internal_number}
                  onChange={(e) => handleInputChange('internal_number', e.target.value)}
                  borderRadius="lg"
                />
                <FormErrorMessage>{errors.internal_number}</FormErrorMessage>
              </FormControl>
            </HStack>
            
            <Divider />
            
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="is-manager" mb="0" fontSize="sm" fontWeight="medium">
                Yönetici Yetkisi
              </FormLabel>
              <Switch
                id="is-manager"
                isChecked={formData.is_manager}
                onChange={(e) => handleInputChange('is_manager', e.target.checked)}
                colorScheme="blue"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={loading}
              loadingText="Güncelleniyor..."
              borderRadius="lg"
            >
              Güncelle
            </Button>
            <Button
              variant="ghost"
              onClick={disclosure.onClose}
              borderRadius="lg"
            >
              Vazgeç
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Delete Employee Modal
const DeleteEmployeeModal = ({ employee, disclosure, revalidator }) => {
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const cancelRef = React.useRef();

  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await client.delete(`/employees/${employee?.id}/`);
      
      toast({
        title: 'Başarılı',
        description: 'Personel kaydı silindi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      disclosure.onClose();
      revalidator.revalidate();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Silme işlemi sırasında bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog
      isOpen={disclosure.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={disclosure.onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Personeli Sil
          </AlertDialogHeader>
          <AlertDialogBody>
            <VStack spacing={3} align="start">
              <Text>
                <strong>{employee?.user ? getFullName(employee.user) : 'Bilinmeyen Personel'}</strong> adlı personeli silmek istediğinizden emin misiniz?
              </Text>
              <Text fontSize="sm" color="red.500">
                Bu işlem geri alınamaz ve personelin tüm verileri silinecektir.
              </Text>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={disclosure.onClose}>
              Vazgeç
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              ml={3}
              isLoading={loading}
              loadingText="Siliniyor..."
            >
              Sil
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

function EmployeeNameWithAvatar({ employee }) {
  return (
    <Flex alignItems={'center'}>
      <Avatar mr={4} name={employee.user.first_name} src={employee.avatar_small} />
      <Box>
        <Text mb={2}>{getFullName(employee.user)}</Text>
      </Box>
    </Flex>
  )
}

function TrueFalseIcon({ value }) {
  return (
    <Icon as={value ? MdCheckCircle : MdCancel} color={value ? "green.500" : "red.500"} />
  )
}

export default function EmployeeListTableView({ employees }) {
  const auth = useContext(AuthContext);
  const toast = useToast();
  const revalidator = useRevalidator();
  const addToTeamModalDisclosure = useDisclosure();
  const createTeamModalDisclosure = useDisclosure();
  const removeFromTeamModalDisclosure = useDisclosure();
  const editEmployeeDisclosure = useDisclosure();
  const deleteEmployeeDisclosure = useDisclosure();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);
  
  const bgGradient = "linear(135deg, white 0%, gray.50 100%)";
  const headerBg = "linear(135deg, green.500 0%, teal.600 100%)";
  const cardBg = "white";
  const tableBg = "gray.50";
  const rowHoverBg = "green.50";
  const textColor = "gray.700";
  const borderColor = "gray.200";
  const removeFromTeamCallback = (membership, disclosure) => {
    client
      .delete(`/members/${urlToId(membership.url)}/`)
      .then(async () => {
        revalidator.revalidate();
        toast({
          title: "Başarılı",
          description: `Personel takımdan çıkartıldı`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        disclosure.onClose()
      })
      .catch(() => {
        toast({
          title: "Hata",
          description: "Takımdan çıkarma işlemi başarısız oldu",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };
  const handleAddToTeamButtonClick = (employee) => {
    setSelectedEmployee(employee);
    addToTeamModalDisclosure.onOpen();
  };
  const navigate = useNavigate();
  const isSuperUser =  auth.data && auth.data.user && auth.data.user.is_superuser; 
  const isManager = auth.data.user.employee && auth.data.user.employee.is_manager;
  return (
    <Box
      bgGradient={bgGradient}
      borderRadius="2xl"
      p={6}
      shadow="2xl"
      border="1px"
      borderColor={borderColor}
    >
      {/* Modern Header */}
      <Box
        bgGradient={headerBg}
        borderRadius="xl"
        p={6}
        mb={6}
        shadow="lg"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Icon as={FiUsers} color="white" boxSize={6} />
            <VStack align="start" spacing={1}>
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="white"
                letterSpacing="tight"
              >
                Personel Yönetimi
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800">
                Ekip üyeleri ve takım bilgileri
              </Text>
            </VStack>
          </HStack>
          { (isSuperUser || isManager) &&
            <Flex gap={3}>
              <Button
                onClick={createTeamModalDisclosure.onOpen}
                rightIcon={<AddIcon />}
                bgGradient="linear(135deg, white 0%, gray.100 100%)"
                color="gray.700"
                _hover={{
                  bgGradient: "linear(135deg, gray.100 0%, gray.200 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)"
                }}
                transition="all 0.3s ease"
                borderRadius="xl"
                px={6}
                py={3}
                fontWeight="semibold"
              >
                Takım Oluştur
              </Button>
              <Button
                onClick={() => navigate('/calisan-ekle/')}
                rightIcon={<AddIcon />}
                bgGradient="linear(135deg, white 0%, gray.100 100%)"
                color="gray.700"
                _hover={{
                  bgGradient: "linear(135deg, gray.100 0%, gray.200 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)"
                }}
                transition="all 0.3s ease"
                borderRadius="xl"
                px={6}
                py={3}
                fontWeight="semibold"
              >
                Personel Ekle
              </Button>
            </Flex>
          }
        </Flex>
      </Box>

      {/* Content Area */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        shadow="lg"
        overflow="hidden"
        border="1px"
        borderColor={borderColor}
      >
        {employees && employees.length > 0 ? (
          <TableContainer>
            <Table variant="simple" size="md">
              <Thead bg={tableBg}>
                <Tr>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Ad Soyad
                    </Text>
                  </Th>
                  { isSuperUser && 
                    <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                        Bayi
                      </Text>
                    </Th>
                  }
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Bayi Yöneticisi?
                    </Text>
                  </Th>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Personel Aksiyon
                    </Text>
                  </Th>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Takım
                    </Text>
                  </Th>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Takım Lideri?
                    </Text>
                  </Th>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Takım Aksiyon
                    </Text>
                  </Th>
                  <Th py={4} px={6} borderBottom="2px" borderColor={borderColor}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
                      Meşguliyet
                    </Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {employees.map((employee, empIndex) => {
                  const rowSpan = employee.team_memberships.length;
                  if (rowSpan) {
                    return employee.team_memberships.map((membership, index) => {
                      return (
                        <Tr
                          key={`${employee.id}-${index}`}
                          _hover={{
                            bg: rowHoverBg,
                            transform: "translateX(4px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                          }}
                          transition="all 0.3s ease"
                          borderBottom={empIndex === employees.length - 1 && index === rowSpan - 1 ? "none" : "1px"}
                          borderColor={borderColor}
                        >
                          { index === 0 && 
                            <>
                              <Td rowSpan={rowSpan} py={4} px={6}>
                                <EmployeeNameWithAvatar employee={employee} />
                              </Td>
                              { isSuperUser && 
                                <Td rowSpan={rowSpan} py={4} px={6}>
                                  <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                                    {employee.reseller_name}
                                  </Badge>
                                </Td>
                              }
                              <Td rowSpan={rowSpan} py={4} px={6}>
                                <TrueFalseIcon value={employee.is_manager}/>
                              </Td>
                              <Td rowSpan={rowSpan} py={4} px={6}>
                                <HStack spacing={2}>
                                  <Button
                                    colorScheme='green'
                                    size="sm"
                                    borderRadius="lg"
                                    _hover={{
                                      transform: "translateY(-2px)",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                                    }}
                                    transition="all 0.3s ease"
                                    onClick={ () => { setSelectedEmployee(employee); addToTeamModalDisclosure.onOpen() }}
                                  >
                                    Takıma Ekle
                                  </Button>
                                  <Menu>
                                    <MenuButton
                                      as={IconButton}
                                      icon={<FiMoreVertical />}
                                      size="sm"
                                      variant="ghost"
                                      borderRadius="lg"
                                    />
                                    <MenuList>
                                      <MenuItem
                                        icon={<FiEdit3 />}
                                        onClick={() => {
                                          setSelectedEmployee(employee);
                                          editEmployeeDisclosure.onOpen();
                                        }}
                                      >
                                        Düzenle
                                      </MenuItem>
                                      <MenuItem
                                        icon={<FiTrash2 />}
                                        color="red.500"
                                        onClick={() => {
                                          setSelectedEmployee(employee);
                                          deleteEmployeeDisclosure.onOpen();
                                        }}
                                      >
                                        Sil
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                </HStack>
                              </Td>
                            </>
                          }
                          <Td py={4} px={6}>
                            <Badge colorScheme="purple" variant="outline" px={3} py={1} borderRadius="full">
                              {membership.team?.name || "-"}
                            </Badge>
                          </Td>
                          <Td py={4} px={6}>
                            <TrueFalseIcon value={membership.is_lead} />
                          </Td>
                          <Td py={4} px={6}>
                            <Button
                              colorScheme='red'
                              size="sm"
                              borderRadius="lg"
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                              }}
                              transition="all 0.3s ease"
                              onClick={() => { setSelectedMembership(membership); setSelectedEmployee(employee); removeFromTeamModalDisclosure.onOpen() }}
                            >
                              Takımdan Kaldır
                            </Button>
                          </Td>
                          { index === 0 && 
                            <Td rowSpan={rowSpan} py={4} px={6}>
                              <RelatedTasks employee={employee} />
                            </Td>
                          }
                        </Tr>
                      )
                    })
                  } else {
                    return (
                      <Tr
                        key={employee.id}
                        _hover={{
                          bg: rowHoverBg,
                          transform: "translateX(4px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                        }}
                        transition="all 0.3s ease"
                        borderBottom={empIndex === employees.length - 1 ? "none" : "1px"}
                        borderColor={borderColor}
                      >
                        <Td py={4} px={6}>
                          <EmployeeNameWithAvatar employee={employee} />
                        </Td>
                        { isSuperUser && 
                          <Td py={4} px={6}>
                            <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                              {employee.reseller_name}
                            </Badge>
                          </Td>
                        }
                        <Td py={4} px={6}>
                          <TrueFalseIcon value={employee.is_manager}/>
                        </Td>
                        <Td py={4} px={6}>
                          <HStack spacing={2}>
                            <Button
                              colorScheme='green'
                              size="sm"
                              borderRadius="lg"
                              _hover={{
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                              }}
                              transition="all 0.3s ease"
                              onClick={ () => { setSelectedEmployee(employee); addToTeamModalDisclosure.onOpen() }}
                            >
                              Takıma Ekle
                            </Button>
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<FiMoreVertical />}
                                size="sm"
                                variant="ghost"
                                borderRadius="lg"
                              />
                              <MenuList>
                                <MenuItem
                                  icon={<FiEdit3 />}
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    editEmployeeDisclosure.onOpen();
                                  }}
                                >
                                  Düzenle
                                </MenuItem>
                                <MenuItem
                                  icon={<FiTrash2 />}
                                  color="red.500"
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    deleteEmployeeDisclosure.onOpen();
                                  }}
                                >
                                  Sil
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                        </Td>
                        <Td py={4} px={6}></Td>
                        <Td py={4} px={6}></Td>
                        <Td py={4} px={6}></Td>
                        <Td py={4} px={6}>
                          <RelatedTasks employee={employee} />
                        </Td>
                      </Tr>
                    )
                  }
                })}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Box p={12} textAlign="center">
            <VStack spacing={4}>
              <Icon as={FiUsers} boxSize={12} color="gray.400" />
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                Henüz personel kaydı bulunmuyor
              </Text>
              <Text fontSize="sm" color="gray.400">
                İlk personeli eklemek için yukarıdaki butonu kullanın
              </Text>
            </VStack>
          </Box>
        )}
      </Box>
      <AddToTeamModal
        disclosure={addToTeamModalDisclosure}
        selectedEmployee={selectedEmployee}
        revalidator={revalidator}
      />
      <CreateTeamModal
        disclosure={createTeamModalDisclosure}
        revalidator={revalidator}
      />
      <RemoveFromTeamModal
        disclosure={removeFromTeamModalDisclosure}
        revalidator={revalidator}
        membership={selectedMembership}
        employee={selectedEmployee}
        callback={removeFromTeamCallback}
      />
      <EditEmployeeModal
        employee={selectedEmployee}
        disclosure={editEmployeeDisclosure}
        revalidator={revalidator}
      />
      <DeleteEmployeeModal
        employee={selectedEmployee}
        disclosure={deleteEmployeeDisclosure}
        revalidator={revalidator}
      />
    </Box>
  );
}

// Export modal components for use in other files
export { AddToTeamModal, CreateTeamModal, EditEmployeeModal, DeleteEmployeeModal, RemoveFromTeamModal };
